import { ref, computed } from 'vue'

/**
 * Debounced tenant-scoped user search. Used by ChatUserSearchBar to find
 * users to start a DM with.
 */

interface SearchUser {
    id: string
    name: string
    avatar: string | null
    role: string
    email?: string
    // Only populated on superadmin searches (crossTenant=true on the response).
    // Lets the picker show "on {tenantName}" badges and pick cross-tenant users.
    tenant?: { id: string; name: string } | null
}

const query = ref('')
const results = ref<SearchUser[]>([])
const loading = ref(false)
let debounceTimer: ReturnType<typeof setTimeout> | null = null
let requestCounter = 0

async function runSearch(q: string, reqId: number) {
    loading.value = true
    try {
        const res = await $fetch<{ users: SearchUser[] }>('/api/chat/users/search', { params: { q } })
        // Only commit if this is the latest request (avoid stale overwrites)
        if (reqId === requestCounter) results.value = res.users || []
    } catch (err) {
        if (reqId === requestCounter) results.value = []
    } finally {
        if (reqId === requestCounter) loading.value = false
    }
}

function setQuery(q: string) {
    query.value = q
    if (debounceTimer) clearTimeout(debounceTimer)
    const trimmed = q.trim()
    if (trimmed.length < 1) {
        requestCounter++ // cancel pending
        results.value = []
        loading.value = false
        return
    }
    const reqId = ++requestCounter
    debounceTimer = setTimeout(() => runSearch(trimmed, reqId), 250)
}

function reset() {
    if (debounceTimer) clearTimeout(debounceTimer)
    requestCounter++
    query.value = ''
    results.value = []
    loading.value = false
}

export function useChatUserSearch() {
    return {
        query: computed(() => query.value),
        results: computed(() => results.value),
        loading: computed(() => loading.value),
        setQuery,
        reset,
    }
}
